import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import { useSchemaKey } from '@/composables/schema'
import { useApiStore } from '@/stores/api'

import { hotReloadStore } from '@/utils/build'

import type { ApplicationFormSource, ApplicationSchema, ApplicationSection } from '@policyfly/schema/types/schemas/application'
import type { FormSchema } from '@policyfly/schema/types/shared/formSchema'
import type { Ref, ComputedRef } from 'vue'

export interface FormApplicationStore {
  file: Ref<ApplicationSchema | null>
  PNI: Ref<string>
  schema: ComputedRef<FormSchema<ApplicationFormSource, ApplicationSection> | null>
  headers: ComputedRef<ApplicationSchema['headers']>
  baseResponses: ComputedRef<NonNullable<ApplicationSchema['baseResponses']>>
  initialForm: ComputedRef<ApplicationSchema['initialForm']>
  banner: ComputedRef<ApplicationSchema['banner'] | null>
  loadData: () => Promise<void>
}

export const useFormApplicationStore = defineStore('formApplication', (): FormApplicationStore => {
  const file: FormApplicationStore['file'] = ref(null)
  const PNI: FormApplicationStore['PNI'] = ref('')

  const schema: FormApplicationStore['schema'] = computed(() => file.value
    ? {
        sections: file.value.sections,
        contextFields: file.value.contextFields,
        rating: file.value.rating,
      } as FormSchema<ApplicationFormSource, ApplicationSection>
    : null,
  )
  const headers: FormApplicationStore['headers'] = computed(() => file.value?.headers ?? {})
  const baseResponses: FormApplicationStore['baseResponses'] = computed(() => file.value?.baseResponses ?? [])
  const initialForm: FormApplicationStore['initialForm'] = computed(() => file.value?.initialForm ?? {})
  const banner: FormApplicationStore['banner'] = computed(() => file.value?.banner ?? null)

  const apiStore = useApiStore()
  const { getKey, updateKey } = useSchemaKey({ data: file })
  const loadData: FormApplicationStore['loadData'] = async () => {
    const { key, shouldLoad } = getKey()
    if (!shouldLoad) return
    file.value = null
    PNI.value = ''
    const result = await apiStore.schema.application(key)
    if (result.type.oneofKind !== 'legacy') {
      throw new Error('Unsupported schema type')
    }
    const mod = JSON.parse(result.type.legacy.json) as ApplicationSchema
    file.value = mod
    updateKey(key)
  }

  return {
    file,
    PNI,
    schema,
    headers,
    baseResponses,
    initialForm,
    banner,
    loadData,
  }
})

hotReloadStore(useFormApplicationStore)
